import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getProductSettings } from '@/api'
import type {
  IApiProductSettings,
  IPlacementSettings,
  ISettings,
  ISettingsStore,
} from '@/types'
import { getAccountId, getPlacementId } from '@/utils'

// Move the helper function to the top so it can be used in the store
const getDefaultPlacementSettings = (): IPlacementSettings => {
  return {
    enabled: true,
    layout: {
      type: 'slider',
      grid: {
        cardsPerRow: 4,
      },
    },
    activeFilters: {},
    activeFilterGroups: 'conditions, year, make, model, trim',
    availableFilterGroups: [],
    excludedFilters: {},
    numberOfVehicles: 4,
    activeVehicles: [],
    sortBy: 'display_price',
    sortDirection: 'asc',
    secondarySorts: [],
    cardSettings: {
      width: '340',
      widthUnit: 'px',
      height: '380',
      heightUnit: 'px',
      imageHeight: '200',
      imageHeightUnit: 'px',
      margin: '4',
      marginUnit: 'px',
      borderRadius: '0',
      borderRadiusUnit: 'px',
    },
    vehicleDetailRows: [],
    callToAction: {
      text: 'View Details',
      label: 'View Details',
      link: '/vehicle/{{vin}}',
      colors: {
        color: '#000000',
        colorHover: '#000000',
        background: 'transparent',
        backgroundHover: 'transparent',
      },
      icon: {
        colors: {
          color: '#000000',
          colorHover: '#000000',
          background: 'transparent',
          backgroundHover: 'transparent',
        },
        sizes: {
          container: '30',
          containerUnit: 'px',
          icon: '30',
          iconUnit: 'px',
        },
      },
      typography: {
        fontFamily: 'inherit',
        fontSize: 14,
        fontSizeUnit: 'px',
        fontStyle: 'normal',
        fontWeight: 'normal',
        letterSpacing: 0,
        letterSpacingUnit: 'px',
        lineHeight: 1.5,
        lineHeightUnit: 'em',
        textDecoration: 'none',
        textTransform: 'none',
        textAlignment: 'left',
      },
      border: {
        width: '1',
        widthUnit: 'px',
        style: 'solid',
        color: '#000',
        hoverColor: '#000',
        radius: '0',
        radiusUnit: 'px',
      },
    },
    sliderArrowSettings: {
      fontSize: '48',
      fontSizeUnit: 'px',
      colors: {
        color: '#000000',
        colorHover: '#000000',
        background: 'transparent',
        backgroundHover: 'transparent',
      },
    },
  }
}

export const useSettingsStore = defineStore('settings', (): ISettingsStore => {
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const initialized = ref(false)
  const placementExists = ref(false)

  // Computed getters
  const accountId = getAccountId()
  const productId = 'micro_srp'
  const placementId = getPlacementId()
  const inventoryIds = ref<number[]>([])
  const placementSettings = ref<IPlacementSettings>({} as IPlacementSettings)

  const updatePlacementSettings = (newSettings: ISettings) => {
    if (!newSettings.placement) {
      console.error('Invalid settings provided.')
      return
    }

    const { placement } = newSettings

    // Add validation to ensure all required properties exist
    const validatedPlacement = ensurePlacementSettings(placement)
    placementSettings.value = validatedPlacement
  }

  const resetError = () => {
    error.value = null
  }

  const fetchSettings = async () => {
    loading.value = true
    error.value = null

    try {
      const productSettings: IApiProductSettings | null =
        await getProductSettings(accountId, productId)

      const data =
        productSettings?.config ?? productSettings?.data ?? productSettings

      // Check if Enable is false or doesn't exist - if so, don't display anything
      if (data?.Enable === false || data?.Enable === undefined) {
        console.log(
          'micro_srp',
          'Placement is disabled or Enable flag not found',
        )
        placementExists.value = false
        initialized.value = false
        return
      }

      if (data?.placements == null) {
        console.error('micro_srp', 'No placements found in settings')
        placementExists.value = false
        initialized.value = false
        return
      }

      if (data != null) {
        const ids = data?.a5AccountId ?? []
        inventoryIds.value = Array.isArray(ids) ? ids : [ids]
      }

      const placementSettings: IPlacementSettings = data.placements[placementId]

      // Check if placementSettings exists, if not don't initialize
      if (!placementSettings) {
        console.warn('micro_srp', `No placement found for ID: ${placementId}`)
        placementExists.value = false
        initialized.value = false
        return
      }

      placementExists.value = true
      const settings: ISettings = {
        placement: placementSettings,
      }

      updatePlacementSettings(settings)
      initialized.value = true
    } catch (err) {
      console.error('micro_srp', 'fetch settings', err)
      error.value =
        err instanceof Error ? err : new Error('Unknown error occurred')
      placementExists.value = false
      initialized.value = false
      throw error.value
    } finally {
      loading.value = false
    }
  }

  return {
    // State - return refs directly
    accountId,
    inventoryIds,
    placementId,
    placementSettings,
    loading,
    error,
    initialized,
    placementExists,

    // Actions
    fetchSettings,
    resetError,
  }
})

// Add this function to ensure all required properties exist
const ensurePlacementSettings = (settings: any): IPlacementSettings => {
  if (!settings) {
    return getDefaultPlacementSettings()
  }

  return {
    enabled: settings.enabled ?? true,
    layout: {
      type: settings.layout?.type ?? 'slider',
      grid: {
        cardsPerRow: settings.layout?.grid?.cardsPerRow ?? 4,
      },
    },
    activeFilters: settings.activeFilters ?? {},
    activeFilterGroups:
      settings.activeFilterGroups ?? 'conditions, year, make, model, trim',
    availableFilterGroups: settings.availableFilterGroups ?? [],
    excludedFilters: settings.excludedFilters ?? {},
    numberOfVehicles: settings.numberOfVehicles ?? 4,
    activeVehicles: settings.activeVehicles ?? [],
    sortBy: settings.sortBy ?? 'display_price',
    sortDirection: settings.sortDirection ?? 'asc',
    secondarySorts: settings.secondarySorts ?? [],
    cardSettings: {
      width: settings.cardSettings?.width ?? '340',
      widthUnit: settings.cardSettings?.widthUnit ?? 'px',
      height: settings.cardSettings?.height ?? '380',
      heightUnit: settings.cardSettings?.heightUnit ?? 'px',
      imageHeight: settings.cardSettings?.imageHeight ?? '200',
      imageHeightUnit: settings.cardSettings?.imageHeightUnit ?? 'px',
      margin: settings.cardSettings?.margin ?? '4',
      marginUnit: settings.cardSettings?.marginUnit ?? 'px',
      borderRadius: settings.cardSettings?.borderRadius ?? '0',
      borderRadiusUnit: settings.cardSettings?.borderRadiusUnit ?? 'px',
    },
    vehicleDetailRows: settings.vehicleDetailRows ?? [],
    callToAction: {
      text: settings.callToAction?.text ?? 'View Details',
      label: settings.callToAction?.label ?? 'View Details',
      link: settings.callToAction?.link ?? '/vehicle/{{vin}}',
      colors: {
        color: settings.callToAction?.colors?.color ?? '#000000',
        colorHover: settings.callToAction?.colors?.colorHover ?? '#000000',
        background: settings.callToAction?.colors?.background ?? 'transparent',
        backgroundHover:
          settings.callToAction?.colors?.backgroundHover ?? 'transparent',
      },
      icon: {
        colors: {
          color: settings.callToAction?.icon?.colors?.color ?? '#000000',
          colorHover: settings.callToAction?.icon?.colors?.colorHover ?? '#000000',
          background: settings.callToAction?.icon?.colors?.background ?? 'transparent',
          backgroundHover: settings.callToAction?.icon?.colors?.backgroundHover ?? 'transparent',
        },
        sizes: {
          container: settings.callToAction?.icon?.sizes?.container ?? '30',
          containerUnit: settings.callToAction?.icon?.sizes?.containerUnit ?? 'px',
          icon: settings.callToAction?.icon?.sizes?.icon ?? '30',
          iconUnit: settings.callToAction?.icon?.sizes?.iconUnit ?? 'px',
        },
      },
      typography: {
        fontFamily: settings.callToAction?.typography?.fontFamily ?? 'inherit',
        fontSize: settings.callToAction?.typography?.fontSize ?? 14,
        fontSizeUnit: settings.callToAction?.typography?.fontSizeUnit ?? 'px',
        fontStyle: settings.callToAction?.typography?.fontStyle ?? 'normal',
        fontWeight: settings.callToAction?.typography?.fontWeight ?? 'normal',
        letterSpacing: settings.callToAction?.typography?.letterSpacing ?? 0,
        letterSpacingUnit:
          settings.callToAction?.typography?.letterSpacingUnit ?? 'px',
        lineHeight: settings.callToAction?.typography?.lineHeight ?? 1.5,
        lineHeightUnit:
          settings.callToAction?.typography?.lineHeightUnit ?? 'em',
        textDecoration:
          settings.callToAction?.typography?.textDecoration ?? 'none',
        textTransform:
          settings.callToAction?.typography?.textTransform ?? 'none',
        textAlignment:
          settings.callToAction?.typography?.textAlignment ?? 'left',
      },
      border: {
        width: settings.callToAction?.border?.width ?? '1',
        widthUnit: settings.callToAction?.border?.widthUnit ?? 'px',
        style: settings.callToAction?.border?.style ?? 'solid',
        color: settings.callToAction?.border?.color ?? '#000',
        hoverColor: settings.callToAction?.border?.hoverColor ?? '#000',
        radius: settings.callToAction?.border?.radius ?? '0',
        radiusUnit: settings.callToAction?.border?.radiusUnit ?? 'px',
      },
    },
    sliderArrowSettings: {
      fontSize: settings.sliderArrowSettings?.fontSize ?? '48',
      fontSizeUnit: settings.sliderArrowSettings?.fontSizeUnit ?? 'px',
      colors: {
        color: settings.sliderArrowSettings?.colors?.color ?? '#000000',
        colorHover:
          settings.sliderArrowSettings?.colors?.colorHover ?? '#000000',
        background:
          settings.sliderArrowSettings?.colors?.background ?? 'transparent',
        backgroundHover:
          settings.sliderArrowSettings?.colors?.backgroundHover ??
          'transparent',
      },
    },
  }
}
